import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { formatDate, formatNumber } from '@/lib/utils'
import {
  ArrowLeft,
  Building2,
  AppWindow,
  Users,
  Bell,
  Activity,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react'

interface TenantDetail {
  id: string
  name: string
  slug: string
  plan: string
  status: 'active' | 'suspended' | 'pending'
  settings: Record<string, unknown>
  createdAt: string
  updatedAt: string
  stats: {
    applicationsCount: number
    usersCount: number
    notificationsThisMonth: number
    liveSessionsThisMonth: number
  }
  subscription: {
    stripeCustomerId: string | null
    stripeSubscriptionId: string | null
    currentPeriodStart: string | null
    currentPeriodEnd: string | null
  } | null
  applications: Array<{
    id: string
    name: string
    createdAt: string
  }>
}

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tenant, setTenant] = useState<TenantDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    async function fetchTenant() {
      if (!id) return
      try {
        const data = await api.get<TenantDetail>(`/tenants/${id}`)
        setTenant(data)
      } catch (error) {
        console.error('Failed to fetch tenant:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchTenant()
  }, [id])

  const handleSuspend = async () => {
    if (!tenant || !confirm('Are you sure you want to suspend this tenant?')) return
    setActionLoading(true)
    try {
      await api.post(`/tenants/${tenant.id}/suspend`)
      setTenant({ ...tenant, status: 'suspended' })
    } catch (error) {
      console.error('Failed to suspend tenant:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleActivate = async () => {
    if (!tenant) return
    setActionLoading(true)
    try {
      await api.post(`/tenants/${tenant.id}/activate`)
      setTenant({ ...tenant, status: 'active' })
    } catch (error) {
      console.error('Failed to activate tenant:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleChangePlan = async (plan: string) => {
    if (!tenant) return
    setActionLoading(true)
    try {
      await api.patch(`/tenants/${tenant.id}`, { plan })
      setTenant({ ...tenant, plan })
    } catch (error) {
      console.error('Failed to change plan:', error)
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Tenant not found</p>
        <Link to="/tenants" className="text-primary hover:underline mt-2 inline-block">
          Back to tenants
        </Link>
      </div>
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'suspended':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/tenants"
          className="p-2 rounded-lg hover:bg-accent text-muted-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{tenant.name}</h1>
              <p className="text-sm text-muted-foreground">{tenant.slug}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tenant.status === 'active' ? (
            <button
              onClick={handleSuspend}
              disabled={actionLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Suspend'}
            </button>
          ) : (
            <button
              onClick={handleActivate}
              disabled={actionLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate'}
            </button>
          )}
        </div>
      </div>

      {/* Status & Plan */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Status</h3>
          <div className="flex items-center gap-2">
            {getStatusIcon(tenant.status)}
            <span className="font-medium capitalize">{tenant.status}</span>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Plan</h3>
          <div className="flex items-center justify-between">
            <span className="font-medium capitalize">{tenant.plan}</span>
            <select
              value={tenant.plan}
              onChange={(e) => handleChangePlan(e.target.value)}
              disabled={actionLoading}
              className="text-sm rounded-lg border border-input bg-background px-2 py-1"
            >
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AppWindow className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Applications</p>
              <p className="text-xl font-bold">
                {formatNumber(tenant.stats.applicationsCount)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">End Users</p>
              <p className="text-xl font-bold">
                {formatNumber(tenant.stats.usersCount)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-sm text-muted-foreground">Notifications (Month)</p>
              <p className="text-xl font-bold">
                {formatNumber(tenant.stats.notificationsThisMonth)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-sm text-muted-foreground">Live Sessions (Month)</p>
              <p className="text-xl font-bold">
                {formatNumber(tenant.stats.liveSessionsThisMonth)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Info */}
      {tenant.subscription && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Stripe Customer ID</p>
              <p className="font-mono text-sm">
                {tenant.subscription.stripeCustomerId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Subscription ID</p>
              <p className="font-mono text-sm">
                {tenant.subscription.stripeSubscriptionId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Period</p>
              <p className="text-sm">
                {tenant.subscription.currentPeriodStart && tenant.subscription.currentPeriodEnd
                  ? `${formatDate(tenant.subscription.currentPeriodStart)} - ${formatDate(tenant.subscription.currentPeriodEnd)}`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Applications List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="border-b border-border px-4 py-3">
          <h3 className="font-semibold">Applications</h3>
        </div>
        <div className="divide-y divide-border">
          {tenant.applications.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              No applications
            </div>
          ) : (
            tenant.applications.map((app) => (
              <div key={app.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AppWindow className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{app.name}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {formatDate(app.createdAt)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Timestamps */}
      <div className="text-sm text-muted-foreground">
        <p>Created: {formatDate(tenant.createdAt)}</p>
        <p>Last updated: {formatDate(tenant.updatedAt)}</p>
      </div>
    </div>
  )
}
